import { type FC, useMemo } from "react";
import { ArrowRight, Check, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { FlowAssignment } from "types";
import { Button } from "components/ui/button";
import { LoadingAnimation } from "components/ui/loading-animation";
import { getFlowAssignmentImageUrl } from "utils/courseImagePreload";
import { buildFlowPath } from "utils/flowNavigation";
import { CourseCover, stateLabels } from "../coursePresentation";

interface ActivitiesProps {
  assignments: FlowAssignment[];
  loading: boolean;
  error: string | null;
}

const comparableCardCopy = (value: string) => value
  .trim()
  .replace(/^\d+[.)]\s+/, "")
  .replace(/\s+/g, " ")
  .toLowerCase();

export const Activities: FC<ActivitiesProps> = ({
  assignments,
  loading,
  error,
}) => {
  const orderedAssignments = useMemo<FlowAssignment[]>(() => {
    return assignments
      .map((assignment, index) => ({ assignment, index }))
      .sort((left, right) => {
        const leftOrder =
          typeof left.assignment.order === "number"
            ? left.assignment.order
            : Number.MAX_SAFE_INTEGER;
        const rightOrder =
          typeof right.assignment.order === "number"
            ? right.assignment.order
            : Number.MAX_SAFE_INTEGER;

        return leftOrder - rightOrder || left.index - right.index;
      })
      .map(({ assignment }) => assignment);
  }, [assignments]);

  if (loading) {
    return (
      <div className="course-empty" role="status" aria-label="Loading Quick Wins">
        <LoadingAnimation size="lg" />
      </div>
    );
  }

  if (error) {
    return <div className="course-empty" role="alert">{error}</div>;
  }

  if (orderedAssignments.length === 0) {
    return <div className="course-empty" role="status">No quick wins assigned yet.</div>;
  }

  return (
    <div id="quick-wins">
      <div className="course-library-heading">
        <div>
          <h2>Quick Wins</h2>
          <p className="course-muted">
            {assignments.length} assigned · {assignments.filter((assignment) => assignment.is_completed).length} completed
          </p>
        </div>
      </div>
      <div className="course-card-grid">
        {orderedAssignments.map((assignment) => {
          const { flow } = assignment;
          const title = flow.title || flow.name;
          const description = flow.description?.trim();
          const showDescription = description && comparableCardCopy(description) !== comparableCardCopy(title);
          const path = buildFlowPath({ flowName: flow.name });
          const state = assignment.is_completed
            ? "completed"
            : assignment.has_started ? "in_progress" : "not_started";
          const duration = flow.estimated_duration_minutes;
          const progress = assignment.progress_percent;
          const showProgress = state === "in_progress" && !flow.is_quiz &&
            typeof progress === "number" && Number.isFinite(progress);
          const progressPercent = showProgress ? Math.min(100, Math.max(0, Math.round(progress))) : 0;
          const action = state === "completed"
            ? "Review quick win"
            : state === "in_progress" ? "Continue" : "Start quick win";

          return (
            <article className="learner-course-card quick-win-card" key={assignment.id} data-testid={`quick-win-card-${assignment.id}`}>
              <Link to={path} className="course-card-cover-link" aria-label={`Open ${title}`}>
                <CourseCover courseId={flow.id} name={title} source={getFlowAssignmentImageUrl(assignment)} />
              </Link>
              <div className="course-card-body">
                <span className={`course-status course-status-${state}`}>
                  {state === "completed" && <Check size={14} aria-hidden="true" />}
                  {stateLabels[state]}
                </span>
                <h2><Link to={path}>{title}</Link></h2>
                {typeof duration === "number" && Number.isFinite(duration) && duration > 0 && (
                  <p className="course-muted quick-win-duration"><Clock size={14} aria-hidden="true" />{duration} min</p>
                )}
                {showDescription && <p className="course-card-description">{description}</p>}
                <div className="quick-win-card-footer">
                  {showProgress && (
                    <div className="course-progress">
                      <div className="course-progress-track" role="progressbar" aria-label={`${title} progress`} aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100}>
                        <span style={{ width: `${progressPercent}%` }} />
                      </div>
                      <span>{progressPercent}%</span>
                    </div>
                  )}
                  <Button asChild variant={state === "completed" ? "secondary" : "default"}>
                    <Link to={path}>
                      {action}<span className="sr-only">: {title}</span><ArrowRight size={16} aria-hidden="true" />
                    </Link>
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};
